import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concatMap, debounceTime, filter, map, merge } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetBuilderPickId, GroupPickId, PickId } from '../../../core/picks/pick-id';
import BetbuilderActions from '../../bet-builder/actions';
import { getTotalStakesForCurrentType } from '../../betplacement/services/stake/stake-utils';
import BetslipComboBetActions from '../../combo-bet/actions';
import { rewardTokensStateSelector } from '../../reward-tokens/selectors';
import BetslipSingleBetActions, { stakeSource } from '../../single-bet/actions';
import { singleBetStateSelector } from '../../single-bet/selectors';
import { parseStakeFieldStake } from '../../stake/utils';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import { betslipTypeStateSelector, selectBetBuilderPicksState } from '../../types/selectors';
import BetslipErrorsActions from '../../validation/actions';
import { QuickBetActions } from '../quick-bet-actions';
import { quickBetStateSelector, selectLinearQuickBetBuilderState, selectQuickBetContext } from '../quick-bet.selectors';
import { IQuickBetStakeState, IQuickBetState } from '../quick-bet.state';
import { QuickBetUtils } from '../services/quick-bet-utils.service';
import { ensureQuickBetActive } from './helpers';

@Injectable()
export class QuickBetStakeEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private quickBetUtils: QuickBetUtils,
        private betslipConfig: BetslipConfig,
    ) {}

    handleUpdateStakeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.changeStake),
            // Debounce needed when SGP pick is added in QuickBet. The stake has to be set in Single module at first (setDefaultStake$). SPO-80297
            debounceTime(3),
            map((action) => QuickBetActions.updateStake({ stake: action.stake, silent: action.silent })),
        );
    });

    handleUpdateComboStakeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.updateStake),
            concatLatestFrom(() => [
                this.store.select(selectLinearQuickBetBuilderState),
                this.store.select(quickBetStateSelector),
                this.store.select(betslipCurrentTypeSelector),
                this.store.select(betslipTypeStateSelector),
                this.store.select(rewardTokensStateSelector),
            ]),
            filter(
                ([, { pickId }, quickBetState, currentType]) =>
                    !pickId && this.checkQuickBetIsCombo(quickBetState) && currentType === BetslipType.Combo,
            ),
            concatMap(([action, , , , types, rewardTokens]) => {
                const [currentStake] = getTotalStakesForCurrentType(types, rewardTokens);
                const stake = action.stake || (currentStake?.stake?.toString() ?? null);

                const stakeValue = parseStakeFieldStake(stake);

                return [
                    BetslipComboBetActions.setComboBetStake({
                        stake: stakeValue,
                        stakeStr: stake ?? undefined,
                        silent: action.silent,
                    }),
                    QuickBetActions.setStakeState(<IQuickBetStakeState>{
                        stake: stake?.toString(),
                    }),
                ];
            }),
        );
    });

    handleUpdateSingleStakeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.updateStake),
            concatLatestFrom(() => [
                this.store.select(quickBetStateSelector),
                this.store.select(betslipCurrentTypeSelector),
                this.store.select(singleBetStateSelector),
            ]),
            filter(([, quickBetState, currentType]) => !this.checkQuickBetIsCombo(quickBetState) && currentType === BetslipType.Single),
            concatMap(([action, quickBetState, , singleBet]) => {
                const quickBetStakeState = this.quickBetUtils.getStakeState(singleBet, action.stake);
                const stake = quickBetStakeState.stake && quickBetStakeState.stake !== '0' ? quickBetStakeState.stake : null;

                const stakeValue = parseStakeFieldStake(stake);

                return [
                    BetslipSingleBetActions.setSingleBetPickStake({
                        pickId: quickBetState.pickIds[0]?.toString(),
                        stake: stakeValue,
                        stakeSource: stakeSource.QuickBet,
                        stakeStr: stake ?? undefined,
                        silent: action.silent,
                    }),
                    QuickBetActions.setStakeState(quickBetStakeState),
                ];
            }),
        );
    });

    handleLinearBetBuilderUpdateStakeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.updateStake),
            concatLatestFrom(() => [this.store.select(selectLinearQuickBetBuilderState), this.store.select(selectBetBuilderPicksState)]),
            filter(([, { isLinearQuickBetBuilder }]) => isLinearQuickBetBuilder),
            concatMap(([action, { pickId }, picksState]) => {
                const rawPickId = pickId!.toString();

                const currentStake = picksState.picks[rawPickId]?.stake?.toString() ?? null;
                const stake = action.stake || currentStake;

                const stakeValue = parseStakeFieldStake(stake);

                return [
                    BetbuilderActions.setPickStake({
                        stake: stakeValue,
                        stakeStr: stake ?? undefined,
                        pickId: rawPickId,
                        silent: !!action.silent,
                    }),
                    QuickBetActions.setStakeState(<IQuickBetStakeState>{
                        stake: stake?.toString(),
                    }),
                ];
            }),
        );
    });

    handleChangeStakeValidationEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.changeStake),
            map(() => BetslipErrorsActions.validateBetslip()),
        );
    });

    handleUpdateStakeResultResetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.updateStake),
            filter((action) => !action.silent),
            map(() => QuickBetActions.resetResultState()),
        );
    });

    handleStakeUpdates$ = createEffect(() => {
        return merge(
            this.actions$.pipe(
                ofType(BetslipSingleBetActions.setSingleBetPickStake),
                ensureQuickBetActive(this.store),
                map((action) => ({ stake: action.stake, actionBetslipType: BetslipType.Single })),
            ),
            this.actions$.pipe(
                ofType(BetslipSingleBetActions.setSingleBetStake),
                ensureQuickBetActive(this.store),
                map((action) => ({ stake: action.stake, actionBetslipType: BetslipType.Single })),
            ),
            this.actions$.pipe(
                ofType(BetslipComboBetActions.setComboBetStake),
                ensureQuickBetActive(this.store),
                map((action) => ({ stake: action.stake, actionBetslipType: BetslipType.Combo })),
            ),
            this.actions$.pipe(
                ofType(BetbuilderActions.setPickStake),
                ensureQuickBetActive(this.store),
                map((action) => ({ stake: action.stake, actionBetslipType: BetslipType.BetBuilder })),
            ),
        ).pipe(
            concatLatestFrom(() => [this.store.select(selectQuickBetContext)]),
            filter(([{ actionBetslipType }, quickBetContext]) => quickBetContext.betslipType === actionBetslipType),
            map(([{ stake }]) => QuickBetActions.setStakeState({ stake: stake ? stake.toString() : null })),
        );
    });

    private isBetBuilderId(pickId: PickId) {
        return GroupPickId.isId(pickId) || (this.betslipConfig.isSportcastAsComboEnabled && BetBuilderPickId.isId(pickId));
    }

    checkQuickBetIsCombo(quickBetState: IQuickBetState) {
        if (!quickBetState.pickIds.length || (quickBetState.pickIds.length === 1 && !this.isBetBuilderId(quickBetState.pickIds[0]))) {
            return false;
        }

        return true;
    }
}
